import { html, nothing, render } from 'lit-html';

import { on } from '@disy/lib/event-util';
import { cadenzaSessionStorage } from '@disy/lib/storage';
import { type Teardown, tearDown } from '@disy/lib/teardown';
import '@disy/ui/pagination-control-status';
import '@disy/ui/pagination-control-stepper';

import './pagination-control-page-size-picker';
import './pagination-control.css';

const COMPONENT_NAME = 'd-pagination-control';
const SESSION_KEY_PREFIX = COMPONENT_NAME + '--';
const DEFAULT_KEY_FOR_SESSION = undefined;
const DEFAULT_ROW_OFFSET = 0;
const DEFAULT_ROW_COUNT = 0;

export const DEFAULT_PAGE_SIZE = 25;
export const DEFAULT_PAGE_SIZE_CONDITIONAL_FOOTER = 10;

export class PaginationControl extends HTMLElement {
  static get observedAttributes() {
    return ['page-size', 'row-offset', 'row-count', 'key-for-session', 'configurable'];
  }

  #teardowns: Teardown[] = [];

  connectedCallback() {
    this.classList.add(COMPONENT_NAME);

    // Read out attributes and session storage to properties
    this.attributeChangedCallback();

    // Make initial value persistent if not already
    this.#persistPageSizeInSessionStorage(this.pageSize);

    // Render
    this.#render();

    // Setup change listener
    this.#teardowns = [
      on(this, 'changePageSize', ({ detail: { pageSize } }) => {
        if (pageSize) {
          this.pageSize = pageSize;
        }
      }),
    ];
  }

  disconnectedCallback() {
    this.#teardowns = tearDown(this.#teardowns);
  }

  attributeChangedCallback() {
    // Render only when already having a parent because attributeChangedCallback() may be called multiple times on
    // construction via hyperscript
    if (this.parentNode) {
      this.#render();
    }
  }

  #render() {
    const { rowOffset, rowCount, pageSize } = this;
    const start: number = rowOffset + 1;
    const end: number = Math.min(rowCount, rowOffset + pageSize);
    // the classes use the component name to make webpack see the dependency
    render(
      html`
        <div class="${COMPONENT_NAME}--footer">
          <div class="d-stack-h space-3">
            ${this.configurable ?
              html`<d-pagination-control-page-size-picker
                options="${this.#rawPageSizeOptions ?? nothing}"
                value="${this.pageSize}"
              ></d-pagination-control-page-size-picker>`
            : nothing}
            <d-pagination-control-status start="${start}" end="${end}" max="${rowCount}"></d-pagination-control-status>
          </div>
          <d-pagination-control-stepper
            current-page="${this.currentPage}"
            page-count="${this.pageCount}"
            aria-controls=${this.ariaControls}
          ></d-pagination-control-stepper>
        </div>
      `,
      this,
    );
  }

  get #rawPageSizeOptions() {
    return this.getAttribute('page-size-options');
  }

  get ariaControls() {
    return this.getAttribute('aria-controls') ?? this.id;
  }

  get currentPage() {
    return Math.ceil(this.rowOffset / this.pageSize + 1);
  }

  get pageCount() {
    // if we are not on a pageSize separation and there are fewer
    // rows than the pageSize, both the first and the last pages
    // are partial, so we need one more page.
    const { rowCount, pageSize, rowOffset } = this;
    const addPartialPageToTotal = rowCount < pageSize && rowOffset > 0 ? 1 : 0;
    const pageCount = Math.ceil(rowCount / pageSize + addPartialPageToTotal);
    return pageCount || 1;
  }

  get keyForSession() {
    return this.getAttribute('key-for-session') ?? DEFAULT_KEY_FOR_SESSION;
  }

  get pageSize() {
    let pageSizeString;

    const keyForSession = this.keyForSession;
    if (keyForSession) {
      pageSizeString = cadenzaSessionStorage.getItem(SESSION_KEY_PREFIX + keyForSession);
    }

    if (!pageSizeString) {
      pageSizeString = this.getAttribute('page-size');
    }

    if (pageSizeString) {
      return parseInt(pageSizeString);
    }

    return DEFAULT_PAGE_SIZE;
  }

  set pageSize(pageSize) {
    this.setAttribute('page-size', String(pageSize));
    this.#persistPageSizeInSessionStorage(pageSize);
  }

  #persistPageSizeInSessionStorage(pageSize: number) {
    const keyForSession = this.keyForSession;
    if (keyForSession) {
      cadenzaSessionStorage.setItem(SESSION_KEY_PREFIX + keyForSession, String(pageSize));
    }
  }

  get rowOffset() {
    const attributeValue = this.getAttribute('row-offset');
    return attributeValue ? Number.parseInt(attributeValue) : DEFAULT_ROW_OFFSET;
  }

  get rowCount() {
    const attributeValue = this.getAttribute('row-count');
    return attributeValue ? Number.parseInt(attributeValue) : DEFAULT_ROW_COUNT;
  }

  get configurable() {
    return this.hasAttribute('configurable');
  }
}

customElements.define(COMPONENT_NAME, PaginationControl);
declare global {
  interface HTMLElementTagNameMap {
    [COMPONENT_NAME]: PaginationControl;
  }
}
